import { useState, useEffect } from "react";
import axios from "axios";
import DevisDetailModal from "./DevisDetailModal";
import { CheckCircle } from "lucide-react"; // ou selon votre configuration d'icônes
import { useNavigate, useLocation } from "react-router-dom";
import {
  Calendar,
  Plus,
  Trash2,
  AlertCircle,
  Check,
  Search,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

import {
  Tooltip,
  PieChart,
  Pie,
  Legend,
  Cell,
  ResponsiveContainer,
} from "recharts";
import {
  ArrowUp,
  ArrowDown,
  Users,
  FileText,
  Clock,
  Building2,
} from "lucide-react";

// Composant de pagination réutilisable
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  return (
    <div className="flex justify-center items-center mt-6 space-x-2">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Précédent
      </button>

      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          className={`px-3 py-1 rounded border ${
            currentPage === page ? "bg-blue-500 text-white" : "bg-white"
          }`}
        >
          {page}
        </button>
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Suivant
      </button>
    </div>
  );
};

// Composant de barre de recherche et filtres
const SearchAndFilterBar = ({
  searchTerm,
  onSearchChange,
  filters,
  onFilterChange,
  groupByOptions,
  groupBy,
  onGroupByChange,
  sortOptions,
  sortBy,
  onSortChange,
  sortDirection,
  onSortDirectionChange,
}) => {
  return (
    <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
        {/* Barre de recherche */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Filtres */}
        {filters &&
          filters.map((filter, index) => (
            <div key={index} className="min-w-[150px]">
              <select
                value={filter.value}
                onChange={(e) => onFilterChange(filter.key, e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{filter.placeholder}</option>
                {filter.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ))}

        {/* Regroupement */}
        {groupByOptions && (
          <div className="min-w-[150px]">
            <select
              value={groupBy}
              onChange={(e) => onGroupByChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Regrouper par</option>
              {groupByOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Tri */}
        {sortOptions && (
          <div className="flex items-center gap-2 min-w-[150px]">
            <select
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value)}
              className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Trier par</option>
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              onClick={onSortDirectionChange}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {sortDirection === "asc" ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Fonction utilitaire pour filtrer et trier les données
const filterAndSortData = (
  data,
  searchTerm,
  filters,
  sortBy,
  sortDirection,
  searchFields
) => {
  return data
    .filter((item) => {
      // Filtre par recherche texte
      const matchesSearch =
        !searchTerm ||
        searchFields.some((field) =>
          String(item[field] || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );

      // Filtre par filtres sélectionnés
      const matchesFilters = Object.entries(filters).every(
        ([key, value]) => !value || String(item[key] || "") === value
      );

      return matchesSearch && matchesFilters;
    })
    .sort((a, b) => {
      if (!sortBy) return 0;

      const aValue = a[sortBy];
      const bValue = b[sortBy];

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortDirection === "asc"
        ? aValue > bValue
          ? 1
          : -1
        : aValue < bValue
        ? 1
        : -1;
    });
};

const Sidebar = ({ activeTab, setActiveTab }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const userId = currentUser?.id || "me"; // fallback "me" si jamais pas d'id

  const menuItems = [
    {
      id: "dashboard",
      label: "Tableau de bord",
      icon: "📊",
      path: `/admin/${userId}`,
    },
    {
      id: "users",
      label: "Utilisateurs",
      icon: "👥",
      path: `/admin/users/${userId}`,
    },
    {
      id: "dates complets",
      label: "Dates complets",
      icon: "📅",
      path: "/admin/dates-complets",
    },
    {
      id: "quotations",
      label: "Devis",
      icon: "📋",
      path: `/admin/quotations/${userId}`,
    },
    {
      id: "company",
      label: "Entreprises",
      icon: "🏢",
      path: `/admin/company/${userId}`,
    },
  ];

  const handleLogout = () => {
    // Supprimer le token et l'utilisateur du stockage local
    localStorage.removeItem("authToken");
    localStorage.removeItem("currentUser");

    // Supprimer aussi l'Authorization par défaut d'axios si tu l'utilises
    // delete apiClient.defaults.headers.common["Authorization"];

    // Rediriger vers la page de connexion
    window.location.href = "/login";
  };
  const handleMenuClick = (item) => {
    // CORRECTION 2: Navigation améliorée
    console.log("Navigation vers:", item.path, "- Tab:", item.id); // Debug
    setActiveTab(item.id);
    navigate(item.path);
    setIsOpen(false);
  };

  return (
    <>
      <div className="md:hidden fixed left-0 right-0 flex items-center p-4 z-50 bg-white shadow-sm">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="text-2xl focus:outline-none mr-3"
        >
          {isOpen ? "✖" : "☰"}
        </button>
        <h2 className="font-bold text-lg">Menu</h2>
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        ></div>
      )}

      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white rounded-r-3xl shadow-2xl z-50 transform transition-transform duration-300 ease-in-out
        ${isOpen ? "translate-x-0" : "-translate-x-full"}
        md:translate-x-0 md:relative md:h-screen md:rounded-none md:shadow-none`}
      >
        <br />
        <br />
        <br />
        <nav className="p-4 flex flex-col gap-4 items-center">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleMenuClick(item)}
              className={`flex flex-col items-center justify-center gap-2 p-3 rounded-xl transition-all duration-200 w-full
                ${
                  activeTab === item.id
                    ? "bg-blue-100 text-blue-700 font-semibold shadow-lg"
                    : "text-gray-600 hover:bg-gray-50 hover:shadow"
                }`}
            >
              <span className="text-3xl">{item.icon}</span>
              <span className="text-md font-medium">{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>
    </>
  );
};

export default function AdminPage() {
  // États pour la recherche, filtres et pagination
  const [filterServiceType, setFilterServiceType] = useState("");
  const [filteredDates, setFilteredDates] = useState([]);
  const [usersSearchTerm, setUsersSearchTerm] = useState("");
  const [usersFilters, setUsersFilters] = useState({ role: "" });
  const [usersSortBy, setUsersSortBy] = useState("createdAt");
  const [usersSortDirection, setUsersSortDirection] = useState("desc");
  const [usersGroupBy, setUsersGroupBy] = useState("");
  const [usersCurrentPage, setUsersCurrentPage] = useState(1);
  const [zoomedImage, setZoomedImage] = useState(null);

  const [quotationsSearchTerm, setQuotationsSearchTerm] = useState("");
  const [quotationsFilters, setQuotationsFilters] = useState({ status: "" });
  const [quotationsSortBy, setQuotationsSortBy] = useState("createdAt");
  const [quotationsSortDirection, setQuotationsSortDirection] =
    useState("desc");
  const [quotationsGroupBy, setQuotationsGroupBy] = useState("");
  const [quotationsCurrentPage, setQuotationsCurrentPage] = useState(1);

  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [companyFilters, setCompanyFilters] = useState({ statut: "" });
  const [companySortBy, setCompanySortBy] = useState("createdAt");
  const [companySortDirection, setCompanySortDirection] = useState("desc");
  const [companyGroupBy, setCompanyGroupBy] = useState("");
  const [companyCurrentPage, setCompanyCurrentPage] = useState(1);

  // État pour l'envoi d'emails
  const [sendingEmails, setSendingEmails] = useState({});
  // Dans votre composant PageAdmin, ajoutez :
  const [categoriesData, setCategoriesData] = useState([]);

  // Charger les catégories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/category`);
        const result = await response.json();
        if (result.data && result.data.categories) {
          setCategoriesData(result.data.categories);
        }
      } catch (error) {
        console.error("Erreur chargement catégories:", error);
      }
    };

    fetchCategories();
  }, []);
  const updateDevis = async (id, devis) => {
    try {
      const res = await fetch(`${API_BASE_URL}/demandes/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ devis }),
      });

      if (!res.ok) {
        throw new Error("Erreur lors de la mise à jour du devis");
      }

      console.log("✅ Devis mis à jour avec succès");
    } catch (error) {
      console.error("❌ Erreur update devis:", error);
    }
  };

  const handleQuotationsFilterChange = (key, value) => {
    setQuotationsFilters((prev) => ({ ...prev, [key]: value }));
    setQuotationsCurrentPage(1);
  };

  const handleCompanyFilterChange = (key, value) => {
    setCompanyFilters((prev) => ({ ...prev, [key]: value }));
    setCompanyCurrentPage(1);
  };

  // Fonctions pour gérer le tri
  const toggleUsersSortDirection = () => {
    setUsersSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const toggleQuotationsSortDirection = () => {
    setQuotationsSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const toggleCompanySortDirection = () => {
    setCompanySortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  // Options pour les filtres et regroupements
  const usersFilterOptions = [
    {
      key: "role",
      placeholder: "Filtrer par rôle",
      options: [
        { value: "USER", label: "Utilisateur" },
        { value: "ADMIN", label: "Administrateur" },
      ],
    },
  ];

  const usersGroupByOptions = [
    { value: "role", label: "Rôle" },
    { value: "createdAt", label: "Date de création" },
  ];

  const usersSortOptions = [
    { value: "firstName", label: "Prénom" },
    { value: "lastName", label: "Nom" },
    { value: "email", label: "Email" },
    { value: "createdAt", label: "Date de création" },
  ];

  const quotationsFilterOptions = [
  {
    key: "statut",
    placeholder: "Filtrer par statut",
    options: [
      { value: "", label: "Tous les statuts" },
      { value: "approuvé", label: "Approuvé" },
      { value: "pending", label: "En attente" },
      { value: "refusée", label: "Refusée" },
    ],
  },
];

  const quotationsGroupByOptions = [
    { value: "status", label: "Statut" },
    { value: "serviceType", label: "Type de service" },
    { value: "serviceDate", label: "Date de service" },
  ];

  const quotationsSortOptions = [
    { value: "serviceDate", label: "Date de service" },
    { value: "createdAt", label: "Date de création" },
    { value: "status", label: "Statut" },
  ];

  const companyFilterOptions = [
    {
      key: "statut",
      placeholder: "Filtrer par statut",
      options: [
        { value: "Approuvé", label: "Approuvé" },
        { value: "En cours", label: "En cours" },
        { value: "Refusée", label: "Refusée" },
      ],
    },
  ];

  const companyGroupByOptions = [
    { value: "statut", label: "Statut" },
    { value: "service", label: "Service" },
    { value: "typeCollaboration", label: "Type de collaboration" },
  ];

  const companySortOptions = [
    { value: "nom", label: "Nom de l'entreprise" },
    { value: "createdAt", label: "Date de création" },
    { value: "statut", label: "Statut" },
  ];

  // Fonctions pour filtrer, trier et paginer les données
  const getFilteredUsers = () => {
    return filterAndSortData(
      users,
      usersSearchTerm,
      usersFilters,
      usersSortBy,
      usersSortDirection,
      ["firstName", "lastName", "email", "tel"]
    );
  };

  const getFilteredQuotations = () => {
  let filtered = demandes;

  // 1. Filtre par recherche texte
  if (quotationsSearchTerm) {
    const searchTermLower = quotationsSearchTerm.toLowerCase();
    filtered = filtered.filter((demande) => {
      const searchFields = [
        demande.user?.firstName,
        demande.user?.lastName,
        demande.serviceType,
        demande.departureAddress?.city,
        demande.arrivalAddress?.city,
        demande.pickupAddress?.city,
        demande.notes
      ];
      return searchFields.some(field => 
        field && String(field).toLowerCase().includes(searchTermLower)
      );
    });
  }

  // 2. Filtre par statut
  if (quotationsFilters.statut) {
    filtered = filtered.filter((demande) => {
      const statut = demande.statut || demande.status || "";
      return statut.toLowerCase().includes(quotationsFilters.statut.toLowerCase());
    });
  }

  // 3. Tri
  if (quotationsSortBy) {
    filtered = [...filtered].sort((a, b) => {
      const aValue = a[quotationsSortBy];
      const bValue = b[quotationsSortBy];
      
      if (quotationsSortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }

  return filtered;
};

  const getFilteredCompanies = () => {
    return filterAndSortData(
      requests,
      companySearchTerm,
      companyFilters,
      companySortBy,
      companySortDirection,
      ["nom", "email", "service", "typeCollaboration"]
    );
  };

  // Fonctions pour grouper les données
  const groupData = (data, groupBy) => {
    if (!groupBy) return { Tous: data };

    const grouped = {};
    data.forEach((item) => {
      const key = item[groupBy] || "Non spécifié";
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(item);
    });

    return grouped;
  };

  // Fonctions pour paginer les données
  const paginateData = (data, currentPage, itemsPerPage = 10) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  // Calcul du nombre total de pages
  const getTotalPages = (data) => {
    return Math.ceil(data.length / 10);
  };

  // Rendu des données groupées avec pagination
  const renderGroupedData = (
    groupedData,
    renderItem,
    currentPage,
    onPageChange
  ) => {
    return Object.entries(groupedData).map(([groupName, groupItems]) => {
      const totalPages = getTotalPages(groupItems);
      const paginatedItems = paginateData(groupItems, currentPage);

      return (
        <div key={groupName} className="mb-8">
          <h3 className="text-lg font-semibold mb-4 bg-gray-100 p-3 rounded-lg">
            {groupName} ({groupItems.length})
          </h3>
          {paginatedItems.map(renderItem)}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          )}
        </div>
      );
    });
  };
  const location = useLocation();

  const getActiveTabFromURL = () => {
    const path = location.pathname;
    if (path === "/admin" || path === "/admin/") return "dashboard";
    if (path.startsWith("/admin/users")) return "users";
    if (path.startsWith("/admin/dates-complets")) return "dates complets";
    if (path.startsWith("/admin/quotations")) return "quotations";
    if (path.startsWith("/admin/company")) return "company";
    return "dashboard";
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromURL());
  const [users, setUsers] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [demandes, setDemandes] = useState([]);
  const [selectedDemande, setSelectedDemande] = useState(null);

  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [devisModalDemande, setDevisModalDemande] = useState(null);

  // Formatage des montants du bon de travail (dollars canadiens)
  const fmtMontant = (n) =>
    (Number(n) || 0).toLocaleString("fr-CA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL_PROD // https://demenagementsamyyhaul.ca/api
    : process.env.REACT_APP_API_URL_DEV; // http://158.69.205.231:3000


  useEffect(() => {
    const loadDataForTab = async () => {
      try {
        console.log(`🔄 Chargement des données pour l'onglet: ${activeTab}`);

        // Toujours charger les données de base pour le dashboard
        if (activeTab === "dashboard" || activeTab === "users") {
          await fetchUsers();
        }

        if (activeTab === "dashboard" || activeTab === "quotations") {
          await fetchDemandes();
        }

        if (activeTab === "dashboard" || activeTab === "company") {
          await fetchCollaborations();
        }

        // Chargements spécifiques
        if (activeTab === "dates complets") {
          await fetchFullyBookedDates();
        }
      } catch (error) {
        console.error("❌ Erreur de chargement des données:", error);
      }
    };

    loadDataForTab();
  }, [activeTab]); // ✅ Dépend de activeTab, pas vide

  // ✅ Fonction corrigée pour charger les collaborations
  const fetchCollaborations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/Collaboration`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        throw new Error(`Erreur HTTP: ${res.status}`);
      }

      const data = await res.json();
      console.log("Réponse API collaborations:", data);

      let requestsData = [];

      // CORRECTION: La réponse est directement un tableau
      if (Array.isArray(data)) {
        requestsData = data;
      } else {
        console.warn(
          "Structure de données inattendue pour les collaborations:",
          data
        );
        requestsData = [];
      }

      console.log("Collaborations chargées:", requestsData);
      setRequests(requestsData);
    } catch (error) {
      console.error("Erreur lors du chargement des collaborations:", error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setActiveTab(getActiveTabFromURL());
  }, [location.pathname]);
  useEffect(() => {
    if (activeTab === "quotations") {
      fetchDemandes();
    }
  }, [activeTab]);

  const fetchDemandes = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/demandes`);
      const data = await res.json();

      console.log("Réponse API demandes:", data);

      let demandsData = [];

      // Adapter selon la structure de votre API
      if (data.data && Array.isArray(data.data.demands)) {
        demandsData = data.data.demands;
      } else if (data.data && Array.isArray(data.data)) {
        demandsData = data.data;
      } else if (Array.isArray(data.demands)) {
        demandsData = data.demands;
      } else if (Array.isArray(data)) {
        demandsData = data;
      } else {
        console.warn("Structure de données inattendue");
        demandsData = [];
      }

      // S'assurer que chaque demande a un tableau d'images
      demandsData = demandsData.map((demande) => ({
        ...demande,
        images: demande.images || [], // Garantir que images est toujours un tableau
      }));

      console.log("Demandes chargées avec images:", demandsData);
      setDemandes(demandsData);
    } catch (error) {
      console.error("Erreur fetch demandes:", error);
    }
  };
  // Ajouter cet état
  const [updateLoading, setUpdateLoading] = useState(false);
  const handleUpdateDevis = async (id, devis) => {
    if (!devis || isNaN(devis) || parseFloat(devis) <= 0) {
      alert("⚠️ Veuillez saisir un montant de devis valide");
      return;
    }

    try {
      // Utiliser la route dédiée qui met à jour ET envoie l'email
      const res = await fetch(`${API_BASE_URL}/demandes/${id}/devis`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ devis: parseFloat(devis) }),
      });

      if (res.ok) {
        const data = await res.json();
        await fetchDemandes();
        alert("✅ Devis mis à jour et email envoyé avec succès !");
      } else {
        const errorData = await res.json();
        throw new Error(
          errorData.message || "Erreur lors de la mise à jour du devis"
        );
      }
    } catch (error) {
      console.error("Erreur update devis:", error);
      alert(`❌ Erreur: ${error.message}`);
    }
  };
  // Fonction pour mettre à jour le statut d'une demande
  const updateDemandeStatus = async (id, newStatus) => {
    try {
      setUpdateLoading(true);

      console.log("Mise à jour du statut:", id, "->", newStatus);

      const response = await fetch(`${API_BASE_URL}/demandes/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          statut: newStatus,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      console.log("Statut mis à jour avec succès:", result);

      // Mettre à jour l'état local
      setDemandes((prev) =>
        prev.map((d) => (d.id === id ? { ...d, statut: newStatus } : d))
      );

      return result;
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut:", error);
      throw error;
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleStatusChangeInTable = async (demandeId, newStatus) => {
    if (!demandeId || !newStatus) return;

    try {
      console.log("Changement de statut pour:", demandeId, "->", newStatus);

      // Optionnel: tester d'abord l'API
      // await testStatusUpdate(demandeId, newStatus);

      // Mettre à jour localement immédiatement pour un feedback visuel rapide
      setDemandes((prev) =>
        prev.map((d) => {
          if (d.id === demandeId) {
            // Convertir pour l'affichage local
            let displayStatus;
            switch (newStatus) {
              case "Approuvé":
              case "APPROVED":
              case "CONFIRMED":
                displayStatus = "Approuvé";
                break;
              case "En cours":
              case "PENDING":
                displayStatus = "En cours";
                break;
              case "Refusée":
              case "REJECTED":
                displayStatus = "Refusée";
                break;
              default:
                displayStatus = newStatus;
            }
            return { ...d, statut: displayStatus };
          }
          return d;
        })
      );

      // Envoyer la requête API
      await updateDemandeStatus(demandeId, newStatus);

      console.log(
        `Statut mis à jour pour la demande ${demandeId}: ${newStatus}`
      );
    } catch (error) {
      console.error("Erreur:", error);

      // Recharger les données pour revenir à l'état précédent
      await fetchDemandes();

      alert(`❌ Erreur lors de la mise à jour du statut: ${error.message}`);
    }
  };
  const pendingQuotations = quotations.filter((q) => q.status === "pending");
  const approvedQuotations = quotations.filter((q) => q.status === "approved");
  const rejectedQuotations = quotations.filter((q) => q.status === "rejected");

  const [newDate, setNewDate] = useState("");
  const [fullyBookedDates, setFullyBookedDates] = useState([]);
  const [dateIntervals, setDateIntervals] = useState([]);

  const [newInterval, setNewInterval] = useState({
    serviceType: "demenagement", // Valeur par défaut

    startDate: "",
    endDate: "",
    reason: "",
  });
  const [showIntervalForm, setShowIntervalForm] = useState(false);

  // ---- 📌 Chargement initial - CORRIGÉ ----
  useEffect(() => {
    fetchFullyBookedDates();
  }, []);

  // ---- 📌 Fetch Dates complètes - CORRIGÉ ----
  const fetchFullyBookedDates = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/service-unavailable-date`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      console.log("Réponse complète de l'API:", result);

      // CORRECTION: Adapter à la structure réelle de votre API
      let datesArray = [];

      // Essayez différentes structures possibles
      if (Array.isArray(result)) {
        // Structure: [...]
        datesArray = result;
      } else if (result.data && Array.isArray(result.data)) {
        // Structure: { data: [...] }
        datesArray = result.data;
      } else if (result.data && Array.isArray(result.data.unavailableDates)) {
        // Structure: { data: { unavailableDates: [...] } }
        datesArray = result.data.unavailableDates;
      } else if (
        result.unavailableDates &&
        Array.isArray(result.unavailableDates)
      ) {
        // Structure: { unavailableDates: [...] }
        datesArray = result.unavailableDates;
      } else if (result.data && result.data.unavailableInterval) {
        // Structure pour une seule intervalle: { data: { unavailableInterval: {...} } }
        datesArray = [result.data.unavailableInterval];
      } else if (result.unavailableInterval) {
        // Structure: { unavailableInterval: {...} }
        datesArray = [result.unavailableInterval];
      } else {
        console.warn(
          "Structure de données inattendue, utilisation d'un tableau vide"
        );
        console.warn("Structure reçue:", result);
        datesArray = [];
      }

      console.log("Dates indisponibles extraites:", datesArray);
      setFullyBookedDates(datesArray);
    } catch (error) {
      console.error(
        "Erreur lors du chargement des dates indisponibles:",
        error
      );
      setFullyBookedDates([]);
    }
  };
  // Fonctions de gestion des filtres pour chaque onglet
  const handleUsersFilterChange = (key, value) => {
    setUsersFilters((prev) => ({ ...prev, [key]: value }));
    setUsersCurrentPage(1);
  };
  useEffect(() => {
    if (filterServiceType) {
      const filtered = fullyBookedDates.filter(
        (date) => date.serviceType === filterServiceType
      );
      setFilteredDates(filtered);
    } else {
      setFilteredDates(fullyBookedDates);
    }
  }, [fullyBookedDates, filterServiceType]);
  // ---- 📌 Ajouter une date/intervalle - CORRIGÉ ----
  // ---- 📌 Ajouter une date/intervalle - AVEC SUPPORT "TOUS LES SERVICES" ----
  const addDateInterval = async () => {
    if (!newInterval.startDate || !newInterval.endDate) {
      alert("Veuillez sélectionner les dates de début et de fin");
      return;
    }

    try {
      if (new Date(newInterval.endDate) < new Date(newInterval.startDate)) {
        throw new Error("La date de fin doit être après la date de début");
      }

      // Si "Tous les services" est sélectionné, créer une indisponibilité pour chaque service
      if (newInterval.serviceType === "all") {
        const services = ["demenagement", "livraison", "ramassage"];
        let successCount = 0;
        let errorCount = 0;

        for (const service of services) {
          try {
            const payload = {
              serviceType: service,
              startDate: newInterval.startDate,
              endDate: newInterval.endDate,
            };

            const response = await fetch(
              `${API_BASE_URL}/service-unavailable-date`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
                body: JSON.stringify(payload),
              }
            );

            if (response.ok) {
              successCount++;
            } else {
              errorCount++;
              console.error(`Erreur pour ${service}:`, await response.json());
            }
          } catch (error) {
            errorCount++;
            console.error(`Erreur pour ${service}:`, error);
          }
        }

        // Recharger les données après tous les ajouts
        await fetchFullyBookedDates();

        if (errorCount === 0) {
          alert(
            `✅ Indisponibilité ajoutée avec succès pour tous les services (${successCount}/3)`
          );
        } else {
          alert(
            `⚠️ Indisponibilité partiellement ajoutée: ${successCount} réussite(s), ${errorCount} erreur(s)`
          );
        }
      } else {
        // Cas normal : un seul service
        const payload = {
          serviceType: newInterval.serviceType,
          startDate: newInterval.startDate,
          endDate: newInterval.endDate,
        };

        const response = await fetch(
          `${API_BASE_URL}/service-unavailable-date`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();

          if (response.status === 409) {
            throw new Error(
              "Il y a un conflit avec des dates déjà indisponibles"
            );
          }

          throw new Error(
            errorData.message || `Erreur ${response.status} lors de l'ajout`
          );
        }

        const result = await response.json();
        console.log("Date/intervalle ajouté avec succès:", result);
        alert("✅ Indisponibilité ajoutée avec succès !");
      }

      // Réinitialiser le formulaire dans tous les cas
      setNewInterval({
        serviceType: "demenagement",
        startDate: "",
        endDate: "",
        reason: "",
      });

      // Recharger les données
      await fetchFullyBookedDates();
    } catch (error) {
      console.error("Erreur lors de l'ajout:", error);
      alert(`❌ Erreur: ${error.message}`);
    }
  };

  // ---- 📌 Fetch Intervalles ----
  const fetchDateIntervals = async () => {
    // Pour votre API, c'est le même endpoint qui retourne à la fois
    // les dates simples et les intervalles
    await fetchFullyBookedDates();
  };

  // ---- 📌 Ajouter une date complète ----
  const addFullyBookedDate = async () => {
    if (!newDate) return;

    try {
      // Convertir la date au format ISO 8601
      const isoDate = new Date(newDate).toISOString().split("T")[0];

      const response = await fetch(`${API_BASE_URL}/service-unavailable-date`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          serviceType: "demenagement",
          startDate: isoDate,
          endDate: isoDate,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Gestion spécifique de l'erreur 409 (conflit de dates)
        if (response.status === 409) {
          throw new Error("Cette date est déjà marquée comme indisponible");
        }

        throw new Error(
          errorData.message || `Erreur ${response.status} lors de l'ajout`
        );
      }

      const result = await response.json();
      console.log("Date ajoutée avec succès:", result);

      setNewDate("");
      // Recharger les données après l'ajout
      await fetchFullyBookedDates();

      alert("Date ajoutée avec succès !");
    } catch (error) {
      console.error("Erreur lors de l'ajout de la date:", error);
      alert(`Erreur: ${error.message}`);
    }
  };

  // ---- 📌 Supprimer une date/intervalle ----
  const removeFullyBookedDate = async (interval) => {
    if (
      !window.confirm(
        "Êtes-vous sûr de vouloir supprimer cette indisponibilité ?"
      )
    ) {
      return;
    }

    try {
      // Votre API utilise probablement l'ID pour la suppression
      const dateId = interval.id || interval._id;

      if (!dateId) {
        throw new Error("ID de l'indisponibilité non trouvé");
      }

      const response = await fetch(
        `${API_BASE_URL}/service-unavailable-date/${dateId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erreur lors de la suppression");
      }

      // Recharger les données après suppression
      await fetchFullyBookedDates();

      alert("Indisponibilité supprimée avec succès !");
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      alert(`Erreur: ${error.message}`);
    }
  };

  // ---- 📌 Formater une date ISO - AMÉLIORÉ ----
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Retourner la string originale si la date est invalide
      }

      return date.toLocaleDateString("fr-FR", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      console.error("Erreur de formatage de date:", error);
      return dateString;
    }
  };
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    tel: "",
    password: "",
    role: "USER",
    profilPicture: "",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const handleCreateUser = async () => {
    // Validation des champs obligatoires
    if (
      !newUser.firstName ||
      !newUser.lastName ||
      !newUser.email ||
      !newUser.password
    ) {
      alert("Veuillez remplir tous les champs obligatoires");
      return;
    }

    if (newUser.password.length < 6) {
      alert("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    try {
      setCreateLoading(true);

      const response = await fetch(`${API_BASE_URL}/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          firstName: newUser.firstName,
          lastName: newUser.lastName,
          email: newUser.email,
          tel: newUser.tel,
          password: newUser.password,
          role: newUser.role,
          profilPicture: newUser.profilPicture,
        }),
      });

      if (response.ok) {
        const userData = await response.json();

        // Ajouter le nouvel utilisateur à la liste
        setUsers((prev) => [...prev, userData]);

        // Réinitialiser le formulaire
        setNewUser({
          firstName: "",
          lastName: "",
          email: "",
          tel: "",
          password: "",
          role: "USER",
          profilPicture: "",
        });

        setShowCreateModal(false);
        alert("Utilisateur créé avec succès !");
      } else {
        const errorData = await response.json();
        alert(
          errorData.message || "Erreur lors de la création de l'utilisateur"
        );
      }
    } catch (error) {
      console.error("Erreur création utilisateur:", error);
      alert("Erreur de connexion au serveur");
    } finally {
      setCreateLoading(false);
    }
  };
  // Ajouter cette fonction dans la section des fonctions de gestion des devis
  const handleUpdateQuotationStatus = async (id, newStatus) => {
    try {
      setUpdateLoading(true);

      const response = await fetch(`${API_BASE_URL}/demandes/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        // Mettre à jour l'état local
        setDemandes((prev) =>
          prev.map((d) => (d.id === id ? { ...d, status: newStatus } : d))
        );

        // Si c'est la demande sélectionnée qui est ouverte, la mettre à jour aussi
        if (selectedDemande && selectedDemande.id === id) {
          setSelectedDemande((prev) => ({ ...prev, status: newStatus }));
        }

        alert(`Statut mis à jour avec succès: ${newStatus}`);
      } else {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Erreur lors de la mise à jour du statut"
        );
      }
    } catch (error) {
      console.error("Erreur update status:", error);
      alert(`Erreur lors de la mise à jour du statut: ${error.message}`);
    } finally {
      setUpdateLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const responseData = await response.json();
        console.log("Réponse complète de l'API:", responseData); // Debug

        // Gestion flexible de la structure de réponse
        let usersData = [];

        if (Array.isArray(responseData)) {
          // Si la réponse est directement un tableau
          usersData = responseData;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          // Si la réponse est { data: [...] }
          usersData = responseData.data;
        } else if (
          responseData.data &&
          Array.isArray(responseData.data.users)
        ) {
          // Si la réponse est { data: { users: [...] } }
          usersData = responseData.data.users;
        } else if (responseData.users && Array.isArray(responseData.users)) {
          // Si la réponse est { users: [...] }
          usersData = responseData.users;
        } else {
          console.error("Structure de réponse inattendue:", responseData);
          usersData = [];
        }

        console.log("Utilisateurs extraits:", usersData); // Debug
        setUsers(usersData);
      } else {
        console.error("Erreur HTTP:", response.status);
      }
    } catch (error) {
      console.error("Erreur chargement utilisateurs:", error);
      // En cas d'erreur, initialiser avec un tableau vide pour éviter l'erreur
      setUsers([]);
    }
  };
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const showRequestDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedRequest(null);
  };

  // Appelez cette fonction dans useEffect ou quand l'onglet users est activé
  useEffect(() => {
    if (activeTab === "users") {
      fetchUsers();
    }
  }, [activeTab]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/Collaboration/${id}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ statut: newStatus }),
        }
      );

      if (!response.ok) {
        throw new Error("Erreur lors de la mise à jour du statut");
      }

      const updated = await response.json();

      // Mettre à jour la liste localement
      setRequests((prev) =>
        prev.map((req) =>
          req.id === id ? { ...req, statut: updated.statut } : req
        )
      );
    } catch (error) {
      console.error(error);
    }
  };
  // Fonction pour normaliser tous les statuts
  const normalizeStatus = (status) => {
    const statut = status?.statut || status?.status || status;

    if (
      statut === "Approuvé" ||
      statut === "CONFIRMED" ||
      statut === "APPROVED"
    ) {
      return "Approuvé";
    }
    if (statut === "Refusée" || statut === "REJECTED" || statut === "REFUSED") {
      return "Refusée";
    }
    if (
      statut === "PENDING" ||
      statut === "En cours" ||
      statut === "En attente"
    ) {
      return "En attente";
    }
    return statut || "En attente";
  };

  // Utilisation dans les statistiques simplifiée :
  const getApprovedCount = () =>
    demandes.filter((d) => normalizeStatus(d) === "Approuvé").length;

  const getPendingCount = () =>
    demandes.filter((d) => normalizeStatus(d) === "En attente").length;

  const getRejectedCount = () =>
    demandes.filter((d) => normalizeStatus(d) === "Refusée").length;
  // Fonction pour générer l'activité récente basée sur les données réelles
  const getRecentActivity = () => {
    const activities = [];

    // Dernières demandes approuvées
    const recentApproved = demandes
      .filter((d) => d.statut === "Approuvé" || d.statut === "CONFIRMED")
      .slice(0, 2);

    recentApproved.forEach((demande) => {
      activities.push({
        title: `Demande #${demande.id} approuvée`,
        time: "Aujourd'hui",
        type: "approved",
        amount: demande.devis,
      });
    });

    // Dernières demandes en attente
    const recentPending = demandes
      .filter((d) => d.statut === "PENDING" || d.statut === "En cours")
      .slice(0, 2);

    recentPending.forEach((demande) => {
      activities.push({
        title: `Nouvelle demande #${demande.id}`,
        time: "Aujourd'hui",
        type: "pending",
        amount: null,
      });
    });

    // Informations utilisateurs
    if (users.length > 0) {
      activities.push({
        title: `${users.length} utilisateurs inscrits`,
        time: "Ce mois",
        type: "user",
        amount: null,
      });
    }

    return activities.slice(0, 5); // Limiter à 5 activités
  };

  // Composant DashboardCard corrigé (si nécessaire)
  const DashboardCard = ({
    icon,
    label,
    value,
    change,
    changeType,
    progressValue,
    color,
  }) => {
    const colorClasses = {
      blue: "from-blue-500 to-blue-600",
      purple: "from-purple-500 to-purple-600",
      amber: "from-amber-500 to-amber-600",
      green: "from-green-500 to-green-600",
    };

    return (
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div
            className={`p-3 rounded-xl bg-gradient-to-r ${colorClasses[color]} text-white`}
          >
            {icon}
          </div>
          <span
            className={`text-sm font-semibold px-2 py-1 rounded ${
              changeType === "increase"
                ? "bg-green-100 text-green-800"
                : changeType === "decrease"
                ? "bg-red-100 text-red-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {change}
          </span>
        </div>

        <h3 className="text-2xl font-bold text-gray-900 mb-2">{value}</h3>
        <p className="text-sm text-gray-600 mb-3">{label}</p>

        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full bg-gradient-to-r ${colorClasses[color]}`}
            style={{ width: `${progressValue}%` }}
          ></div>
        </div>
      </div>
    );
  };
  if (loading) {
    return <div className="p-8 text-gray-600">⏳ Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <br />
      <br />
      <br />
      <br />
      <div className="flex">
        <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />

        <main className="flex-1 p-8">
          {activeTab === "dashboard" && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Vue d'ensemble
                </h2>
                <p className="text-gray-600">
                  Tableau de bord administratif - Données en temps réel
                </p>
              </div>

              {/* Cartes de statistiques AVEC DONNÉES RÉELLES */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                {/* Utilisateurs totaux */}
                <DashboardCard
                  icon={<Users className="w-8 h-8" />}
                  label="UTILISATEURS"
                  value={users.length}
                  change={`Dernier: ${
                    users.length > 0
                      ? new Date(
                          users[users.length - 1]?.createdAt
                        ).toLocaleDateString("fr-FR")
                      : "N/A"
                  }`}
                  changeType="neutral"
                  progressValue={Math.min((users.length / 200) * 100, 100)}
                  color="blue"
                />

                {/* Demandes en cours */}
                <DashboardCard
                  icon={<FileText className="w-8 h-8" />}
                  label="DEMANDES EN COURS"
                  value={
                    demandes.filter(
                      (d) =>
                        d.statut === "PENDING" ||
                        d.statut === "En cours" ||
                        d.status === "PENDING"
                    ).length
                  }
                  change={`Dernière: ${
                    demandes.length > 0
                      ? new Date(
                          demandes[demandes.length - 1]?.createdAt
                        ).toLocaleDateString("fr-FR")
                      : "N/A"
                  }`}
                  changeType="neutral"
                  progressValue={Math.min(
                    (demandes.filter(
                      (d) =>
                        d.statut === "PENDING" ||
                        d.statut === "En cours" ||
                        d.status === "PENDING"
                    ).length /
                      demandes.length) *
                      100,
                    100
                  )}
                  color="purple"
                />

                {/* Collaborations en cours */}
                <DashboardCard
                  icon={<Building2 className="w-8 h-8" />}
                  label="COLLABORATIONS EN COURS"
                  value={
                    requests.filter(
                      (req) =>
                        req.statut === "En cours" || req.statut === "PENDING"
                    ).length
                  }
                  change={`Dernière: ${
                    requests.length > 0
                      ? new Date(
                          requests[requests.length - 1]?.createdAt
                        ).toLocaleDateString("fr-FR")
                      : "N/A"
                  }`}
                  changeType="neutral"
                  progressValue={Math.min(
                    (requests.filter(
                      (req) =>
                        req.statut === "En cours" || req.statut === "PENDING"
                    ).length /
                      requests.length) *
                      100,
                    100
                  )}
                  color="amber"
                />

                {/* Total des demandes */}
                <DashboardCard
                  icon={<CheckCircle className="w-8 h-8" />}
                  label="TOTAL DEMANDES"
                  value={demandes.length}
                  change={`+${Math.round(
                    (demandes.length / 100) * 8
                  )}% ce mois`}
                  changeType="increase"
                  progressValue={Math.min((demandes.length / 500) * 100, 100)}
                  color="green"
                />
              </div>
<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
  {/* Graphique des Demandes */}
  <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-200">
    <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
      Répartition des Demandes
    </h3>
    <div className="flex flex-col items-center gap-6">
      {/* Pie Chart */}
      <div className="w-full">
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={[
                {
                  name: "En attente",
                  value: demandes.filter(
                    (d) =>
                      d.statut === "PENDING" ||
                      d.statut === "En cours" ||
                      d.status === "PENDING"
                  ).length,
                },
                {
                  name: "Approuvées",
                  value: demandes.filter(
                    (d) =>
                      d.statut === "Approuvé" ||
                      d.statut === "CONFIRMED" ||
                      d.statut === "APPROVED" ||
                      d.status === "APPROVED" ||
                      d.status === "CONFIRMED"
                  ).length,
                },
                {
                  name: "Refusées",
                  value: demandes.filter(
                    (d) =>
                      d.statut === "Refusée" ||
                      d.statut === "REJECTED" ||
                      d.statut === "REFUSED" ||
                      d.status === "REJECTED"
                  ).length,
                },
              ]}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={110}
              paddingAngle={3}
              dataKey="value"
              label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
              labelLine={true}
            >
              <Cell fill="#f59e0b" />
              <Cell fill="#10b981" />
              <Cell fill="#ef4444" />
            </Pie>
            <Tooltip
              formatter={(value, name) => [`${value} demandes`, name]}
              contentStyle={{
                backgroundColor: "#fff",
                borderRadius: 10,
                padding: 10,
                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Légende en dessous */}
      <div className="flex justify-center gap-6 mt-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          <span className="text-sm font-medium text-gray-700">
            Approuvées:{" "}
            {demandes.filter(
              (d) =>
                d.statut === "Approuvé" ||
                d.statut === "CONFIRMED" ||
                d.statut === "APPROVED" ||
                d.status === "APPROVED" ||
                d.status === "CONFIRMED"
            ).length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
          <span className="text-sm font-medium text-gray-700">
            En attente:{" "}
            {demandes.filter(
              (d) =>
                d.statut === "PENDING" ||
                d.statut === "En cours" ||
                d.status === "PENDING"
            ).length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
          <span className="text-sm font-medium text-gray-700">
            Refusées:{" "}
            {demandes.filter(
              (d) =>
                d.statut === "Refusée" ||
                d.statut === "REJECTED" ||
                d.statut === "REFUSED" ||
                d.status === "REJECTED"
            ).length}
          </span>
        </div>
      </div>
    </div>
  </div>

  {/* Graphique des Collaborations */}
  <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-200">
    <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
      Répartition des Collaborations
    </h3>
    <div className="flex flex-col items-center gap-6">
      {/* Pie Chart */}
      <div className="w-full">
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={[
                {
                  name: "En cours",
                  value: requests.filter(
                    (req) =>
                      req.statut === "En cours" || req.statut === "PENDING"
                  ).length,
                },
                {
                  name: "Approuvées",
                  value: requests.filter((req) => req.statut === "Approuvé")
                    .length,
                },
                {
                  name: "Refusées",
                  value: requests.filter((req) => req.statut === "Refusée")
                    .length,
                },
              ]}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={110}
              paddingAngle={3}
              dataKey="value"
              label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
              labelLine={true}
            >
              <Cell fill="#f59e0b" />
              <Cell fill="#10b981" />
              <Cell fill="#ef4444" />
            </Pie>
            <Tooltip
              formatter={(value, name) => [`${value} collaborations`, name]}
              contentStyle={{
                backgroundColor: "#fff",
                borderRadius: 10,
                padding: 10,
                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Légende en dessous */}
      <div className="flex justify-center gap-6 mt-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          <span className="text-sm font-medium text-gray-700">
            Approuvées:{" "}
            {requests.filter((req) => req.statut === "Approuvé").length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
          <span className="text-sm font-medium text-gray-700">
            En cours:{" "}
            {requests.filter(
              (req) => req.statut === "En cours" || req.statut === "PENDING"
            ).length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
          <span className="text-sm font-medium text-gray-700">
            Refusées:{" "}
            {requests.filter((req) => req.statut === "Refusée").length}
          </span>
        </div>
      </div>
    </div>
  </div>
</div>






              {/* Activité récente avec données réelles */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                  Activité Récente
                </h3>
                <div className="space-y-4">
                  {/* Dernier utilisateur */}
                  {users.length > 0 && (
                    <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          Dernier utilisateur inscrit
                        </p>
                        <p className="text-xs text-gray-500">
                          {users[users.length - 1].firstName}{" "}
                          {users[users.length - 1].lastName} -{" "}
                          {users[users.length - 1].email}
                        </p>
                      </div>
                      <span className="text-sm text-blue-600">
                        {new Date(
                          users[users.length - 1].createdAt
                        ).toLocaleDateString("fr-FR")}
                      </span>
                    </div>
                  )}

                  {/* Dernière demande */}
                  {demandes.length > 0 && (
                    <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                      <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          Dernière demande créée
                        </p>
                        <p className="text-xs text-gray-500">
                          {demandes[demandes.length - 1].serviceType} -{" "}
                          {demandes[demandes.length - 1].user?.firstName ||
                            "Utilisateur invité"}
                        </p>
                      </div>
                      <span className="text-sm text-purple-600">
                        {new Date(
                          demandes[demandes.length - 1].createdAt
                        ).toLocaleDateString("fr-FR")}
                      </span>
                    </div>
                  )}

                  {/* Dernière collaboration */}
                  {requests.length > 0 && (
                    <div className="flex items-center p-3 bg-amber-50 rounded-lg">
                      <div className="w-3 h-3 bg-amber-500 rounded-full mr-3"></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          Dernière demande de collaboration
                        </p>
                        <p className="text-xs text-gray-500">
                          {requests[requests.length - 1].nom} -{" "}
                          {requests[requests.length - 1].service}
                        </p>
                      </div>
                      <span className="text-sm text-amber-600">
                        {new Date(
                          requests[requests.length - 1].createdAt
                        ).toLocaleDateString("fr-FR")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {activeTab === "company" && (
            <div className="bg-gray-50 min-h-screen p-8">
              <h2 className="text-3xl font-bold mb-6 text-gray-800 border-b pb-2">
                Demandes de collaboration
              </h2>

              {/* Statistiques en cartes */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <div className="w-6 h-6 text-green-600">✓</div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm text-green-600 font-medium">
                        Approuvées
                      </p>
                      <p className="text-2xl font-bold text-green-700">
                        {requests.filter((r) => r.statut === "Approuvé").length}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <div className="w-6 h-6 text-yellow-600">⏳</div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm text-yellow-600 font-medium">
                        En cours
                      </p>
                      <p className="text-2xl font-bold text-yellow-700">
                        {requests.filter((r) => r.statut === "En cours").length}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 p-6 rounded-xl border border-red-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <div className="w-6 h-6 text-red-600">✗</div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm text-red-600 font-medium">
                        Refusées
                      </p>
                      <p className="text-2xl font-bold text-red-700">
                        {requests.filter((r) => r.statut === "Refusée").length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <SearchAndFilterBar
                searchTerm={companySearchTerm}
                onSearchChange={setCompanySearchTerm}
                filters={companyFilterOptions}
                onFilterChange={handleCompanyFilterChange}
                groupByOptions={companyGroupByOptions}
                groupBy={companyGroupBy}
                onGroupByChange={setCompanyGroupBy}
                sortOptions={companySortOptions}
                sortBy={companySortBy}
                onSortChange={setCompanySortBy}
                sortDirection={companySortDirection}
                onSortDirectionChange={toggleCompanySortDirection}
              />
              {companyGroupBy ? (
                renderGroupedData(
                  groupData(getFilteredCompanies(), companyGroupBy),
                  (req) => (
                    <tr
                      key={req.id}
                      className="hover:bg-blue-50 transition-colors"
                    >
                      <td className="px-6 py-4 font-medium text-gray-700">
                        {req.nom}
                      </td>
                      <td className="px-6 py-4 text-gray-600">{req.email}</td>
                      <td className="px-6 py-4 text-gray-600">{req.service}</td>
                      <td className="px-6 py-4 text-gray-600">
                        {req.typeCollaboration}
                      </td>

                      {/* Badge Statut */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            req.statut === "Approuvé"
                              ? "bg-green-100 text-green-800"
                              : req.statut === "En cours"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {req.statut}
                        </span>
                      </td>

                      {/* Actions simplifiées */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => showRequestDetails(req)}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-sm"
                        >
                          Détails
                        </button>
                        <select
                          onChange={(e) =>
                            handleStatusChange(req.id, e.target.value)
                          }
                          className="border rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
                          value={req.statut}
                        >
                          <option value="Approuvé">✅ Approuvé</option>
                          <option value="En cours">⏳ En cours</option>
                          <option value="Refusée">❌ Refusée</option>
                        </select>
                        <button
                          onClick={() => updateDevis(req.id, req.devis)}
                          className="text-green-600 hover:text-green-900"
                        >
                          Sauvegarder
                        </button>
                      </td>
                    </tr>
                  ),
                  companyCurrentPage,
                  setCompanyCurrentPage
                )
              ) : (
                <>
             <div className="bg-white rounded-xl shadow-sm overflow-hidden">
  <div className="max-w-full overflow-x-auto md:overflow-visible">
    <table className="min-w-full divide-y divide-gray-200 block md:table">
      {/* En-têtes masqués sur mobile */}
      <thead className="sr-only md:not-sr-only md:table-header-group bg-gray-50">
        <tr className="md:table-row">
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:table-cell">
            Nom de l'entreprise
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:table-cell">
            Email
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:table-cell">
            Service
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:table-cell">
            Type de collaboration
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:table-cell">
            Statut
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:table-cell">
            Actions
          </th>
        </tr>
      </thead>
      
      <tbody className="bg-white divide-y divide-gray-200 block md:table-row-group">
        {paginateData(getFilteredCompanies(), companyCurrentPage).map((req) => (
          <tr 
            key={req.id} 
            className="hover:bg-blue-50 block md:table-row border-b md:border-0 last:border-b-0 mb-4 md:mb-0 transition-colors"
          >
            {/* Colonne Nom */}
            <td 
              data-label="Entreprise" 
              className="px-6 py-4 block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 md:before:hidden"
            >
              <div className="font-medium text-gray-700">{req.nom}</div>
            </td>
            
            {/* Colonne Email */}
            <td 
              data-label="Email" 
              className="px-6 py-4 block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 md:before:hidden"
            >
              <div className="text-gray-600">{req.email}</div>
            </td>
            
            {/* Colonne Service */}
            <td 
              data-label="Service" 
              className="px-6 py-4 block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 md:before:hidden"
            >
              <div className="text-gray-600">{req.service}</div>
            </td>
            
            {/* Colonne Type de collaboration */}
            <td 
              data-label="Type" 
              className="px-6 py-4 block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 md:before:hidden"
            >
              <div className="text-gray-600">{req.typeCollaboration}</div>
            </td>
            
            {/* Colonne Statut */}
            <td 
              data-label="Statut" 
              className="px-6 py-4 block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 md:before:hidden"
            >
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  req.statut === "Approuvé"
                    ? "bg-green-100 text-green-800"
                    : req.statut === "En cours"
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {req.statut}
              </span>
            </td>
            
            {/* Colonne Actions */}
            <td 
              data-label="Actions" 
              className="px-6 py-4 block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 mb-2 md:before:hidden md:mb-0"
            >
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => showRequestDetails(req)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-sm"
                >
                  Détails
                </button>
                <select
                  onChange={(e) => handleStatusChange(req.id, e.target.value)}
                  className="border rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
                  value={req.statut}
                >
                  <option value="Approuvé">✅ Approuvé</option>
                  <option value="En cours">⏳ En cours</option>
                  <option value="Refusée">❌ Refusée</option>
                </select>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>

                  {getTotalPages(getFilteredCompanies()) > 1 && (
                    <Pagination
                      currentPage={companyCurrentPage}
                      totalPages={getTotalPages(getFilteredCompanies())}
                      onPageChange={setCompanyCurrentPage}
                    />
                  )}
                </>
              )}
            </div>
          )}
          {activeTab === "users" && (
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Gestion des utilisateurs
                </h2>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center"
                >
                  <span className="mr-2">+</span>
                  Créer un utilisateur
                </button>
              </div>

              <SearchAndFilterBar
                searchTerm={usersSearchTerm}
                onSearchChange={setUsersSearchTerm}
                filters={usersFilterOptions}
                onFilterChange={handleUsersFilterChange}
                groupByOptions={usersGroupByOptions}
                groupBy={usersGroupBy}
                onGroupByChange={setUsersGroupBy}
                sortOptions={usersSortOptions}
                sortBy={usersSortBy}
                onSortChange={setUsersSortBy}
                sortDirection={usersSortDirection}
                onSortDirectionChange={toggleUsersSortDirection}
              />

              {usersGroupBy ? (
                renderGroupedData(
                  groupData(getFilteredUsers(), usersGroupBy),
                  (user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <img
                              className="h-10 w-10 rounded-full"
                              src={
                                user.profilPicture ||
                                `https://i.pravatar.cc/150?img=${user.id}`
                              }
                              alt={`${user.firstName} ${user.lastName}`}
                            />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.tel}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.role === "ADMIN"
                              ? "bg-purple-100 text-purple-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {user.role === "ADMIN"
                            ? "Administrateur"
                            : "Utilisateur"}
                        </span>
                      </td>
                    </tr>
                  ),
                  usersCurrentPage,
                  setUsersCurrentPage
                )
              ) : (
                <>
            <div className="max-w-full overflow-x-auto md:overflow-visible">
  <table className="min-w-full divide-y divide-gray-200">
    {/* CORRECTION ICI : Pas de "hidden" sur le thead */}
<thead className="sr-only md:not-sr-only md:table-header-group bg-gray-50">
  <tr className="md:table-row">
    {/* SUPPRIMEZ "hidden" de toutes ces classes */}
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:table-cell">
      Utilisateur
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:table-cell">
      Email
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:table-cell">
      Téléphone
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:table-cell">
      Rôle
    </th>
  </tr>
</thead>
    
    <tbody className="bg-white divide-y divide-gray-200 block md:table-row-group">
      {paginateData(getFilteredUsers(), usersCurrentPage).map((user) => (
        <tr 
          key={user.id} 
          className="hover:bg-gray-50 block md:table-row border-b md:border-0 last:border-b-0 mb-4 md:mb-0"
        >
          {/* Colonne Utilisateur */}
          <td 
            data-label="Utilisateur" 
            className="px-6 py-4 block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 md:before:hidden"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0 h-10 w-10">
                <img
                  className="h-10 w-10 rounded-full"
                  src={user.profilPicture || `https://i.pravatar.cc/150?img=${user.id}`}
                  alt={`${user.firstName} ${user.lastName}`}
                />
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-900">
                  {user.firstName} {user.lastName}
                </div>
              </div>
            </div>
          </td>
          
          {/* Colonne Email */}
          <td 
            data-label="Email" 
            className="px-6 py-4 block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 md:before:hidden"
          >
            <div className="text-sm text-gray-500">{user.email}</div>
          </td>
          
          {/* Colonne Téléphone */}
          <td 
            data-label="Téléphone" 
            className="px-6 py-4 block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 md:before:hidden"
          >
            <div className="text-sm text-gray-500">{user.tel || "Non renseigné"}</div>
          </td>
          
          {/* Colonne Rôle */}
          <td 
            data-label="Rôle" 
            className="px-6 py-4 block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 md:before:hidden"
          >
            <span
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                user.role === "ADMIN"
                  ? "bg-purple-100 text-purple-800"
                  : "bg-green-100 text-green-800"
              }`}
            >
              {user.role === "ADMIN" ? "Administrateur" : "Utilisateur"}
            </span>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>

                  {getTotalPages(getFilteredUsers()) > 1 && (
                    <Pagination
                      currentPage={usersCurrentPage}
                      totalPages={getTotalPages(getFilteredUsers())}
                      onPageChange={setUsersCurrentPage}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === "dates complets" && (
            <div className="space-y-8">
              {/* En-tête */}
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-8 h-8 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    Gestion des Dates Indisponibles
                  </h2>
                </div>
                <p className="text-gray-600">
                  Gérez les dates complètement réservées et les intervalles de
                  dates indisponibles pour les services.
                </p>
              </div>

              {/* Section Dates Indisponibles */}
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <AlertCircle className="w-6 h-6 text-red-500" />
                  Dates Indisponibles
                </h3>

                {/* Ajouter une nouvelle date/intervalle */}
                {/* Ajouter une date/intervalle */}
                <div className="bg-red-50 p-6 rounded-xl border border-red-200 mb-6">
                  <h4 className="font-semibold text-red-800 mb-4">
                    Ajouter une date ou un intervalle indisponible
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-red-700 mb-2">
                        Type de service
                      </label>
                      <select
                        value={newInterval.serviceType}
                        onChange={(e) =>
                          setNewInterval({
                            ...newInterval,
                            serviceType: e.target.value,
                          })
                        }
                        className="w-full p-3 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      >
                        <option value="all">Tous les services</option>
                        <option value="demenagement">Déménagement</option>
                        <option value="livraison">Livraison</option>
                        <option value="ramassage">Ramassage</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-red-700 mb-2">
                        Date de début
                      </label>
                      <input
                        type="date"
                        value={newInterval.startDate}
                        onChange={(e) =>
                          setNewInterval({
                            ...newInterval,
                            startDate: e.target.value,
                          })
                        }
                        min={new Date().toISOString().split("T")[0]}
                        className="w-full p-3 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-red-700 mb-2">
                        Date de fin
                      </label>
                      <input
                        type="date"
                        value={newInterval.endDate}
                        onChange={(e) =>
                          setNewInterval({
                            ...newInterval,
                            endDate: e.target.value,
                          })
                        }
                        min={
                          newInterval.startDate ||
                          new Date().toISOString().split("T")[0]
                        }
                        className="w-full p-3 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                  </div>

                  {/* Affichage de ce qui va être créé */}
                  <div className="mb-4 p-3 bg-red-100 rounded-lg">
                    <p className="text-sm text-red-800 font-medium">
                      {newInterval.startDate && newInterval.endDate ? (
                        <>
                          Créer une indisponibilité pour :{" "}
                          <span className="font-bold">
                            {newInterval.serviceType === "all"
                              ? "TOUS LES SERVICES"
                              : newInterval.serviceType === "demenagement"
                              ? "DÉMÉNAGEMENT"
                              : newInterval.serviceType === "livraison"
                              ? "LIVRAISON"
                              : "RAMASSAGE"}
                          </span>
                          <br />
                          {newInterval.startDate === newInterval.endDate
                            ? `Le ${formatDate(newInterval.startDate)}`
                            : `Du ${formatDate(
                                newInterval.startDate
                              )} au ${formatDate(newInterval.endDate)}`}
                        </>
                      ) : (
                        "Sélectionnez les dates pour voir le détail"
                      )}
                    </p>
                  </div>

                  <div className="flex gap-4 items-end">
                    <button
                      onClick={addDateInterval}
                      disabled={!newInterval.startDate || !newInterval.endDate}
                      className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      {newInterval.serviceType === "all"
                        ? "Ajouter pour tous les services"
                        : "Ajouter"}
                    </button>
                  </div>
                </div>

                {/* Filtre par type de service */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filtrer par type de service
                  </label>
                  <select
                    value={filterServiceType}
                    onChange={(e) => setFilterServiceType(e.target.value)}
                    className="w-full md:w-64 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Tous les services</option>
                    <option value="demenagement">Déménagement</option>
                    <option value="livraison">Livraison</option>
                    <option value="ramassage">Ramassage</option>
                  </select>

                  {/* Affichage du comptage des résultats */}
                  {/* Dans l'affichage des dates */}
                </div>

                {/* Liste des dates indisponibles */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-800">
                    Dates actuellement indisponibles (
                    {filterServiceType
                      ? filteredDates.length
                      : fullyBookedDates.length}
                    )
                    {filterServiceType && (
                      <span className="text-sm font-normal text-gray-600 ml-2">
                        (filtré par{" "}
                        {
                          {
                            demenagement: "déménagement",
                            livraison: "livraison",
                            ramassage: "ramassage",
                          }[filterServiceType]
                        }
                        )
                      </span>
                    )}
                  </h4>

                  {(filterServiceType ? filteredDates : fullyBookedDates)
                    .length === 0 ? (
                    <div className="text-center p-8 bg-gray-50 rounded-lg border border-gray-200">
                      <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500 font-medium">
                        {filterServiceType
                          ? `Aucune indisponibilité pour le service sélectionné`
                          : `Aucune date indisponible pour le moment`}
                      </p>
                      {filterServiceType && (
                        <button
                          onClick={() => setFilterServiceType("")}
                          className="text-blue-600 hover:text-blue-800 mt-2"
                        >
                          Voir toutes les indisponibilités
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {(filterServiceType
                        ? filteredDates
                        : fullyBookedDates
                      ).map((interval, index) => {
                        const isSingleDay =
                          interval.startDate === interval.endDate;
                        const serviceTypeLabel =
                          {
                            demenagement: "Déménagement",
                            livraison: "Livraison",
                            ramassage: "Ramassage",
                          }[interval.serviceType] || interval.serviceType;

                        return (
                          <div
                            key={interval.id || index}
                            className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span
                                  className={`text-xs font-medium px-2 py-1 rounded-full ${
                                    interval.serviceType === "demenagement"
                                      ? "bg-red-100 text-red-800"
                                      : interval.serviceType === "livraison"
                                      ? "bg-blue-100 text-blue-800"
                                      : "bg-purple-100 text-purple-800"
                                  }`}
                                >
                                  {serviceTypeLabel}
                                </span>
                              </div>
                              <p className="font-medium text-red-800">
                                {isSingleDay
                                  ? formatDate(interval.startDate)
                                  : `Du ${formatDate(
                                      interval.startDate
                                    )} au ${formatDate(interval.endDate)}`}
                              </p>
                              <p className="text-sm text-red-600">
                                {isSingleDay
                                  ? "Jour complet"
                                  : "Intervalle complet"}
                              </p>
                            </div>
                            <button
                              onClick={() => removeFullyBookedDate(interval)}
                              className="text-red-600 hover:text-red-800 p-2 hover:bg-red-200 rounded-lg transition-colors ml-2"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {activeTab === "quotations" && (
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Gestion des demandes de déménagement
              </h2>
              {/* Statistiques CORRIGÉES avec comptage précis */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                {/* Approuvées */}
                <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Check className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm text-green-600 font-medium">
                        Approuvées
                      </p>
                      <p className="text-2xl font-bold text-green-700">
                        {
                          demandes.filter(
                            (d) =>
                              d.statut === "Approuvé" ||
                              d.status === "Approuvé" ||
                              d.statut === "CONFIRMED" ||
                              d.status === "CONFIRMED" ||
                              d.statut === "APPROVED" ||
                              d.status === "APPROVED"
                          ).length
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* En attente - CORRECTION */}
                <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Clock className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm text-yellow-600 font-medium">
                        En attente
                      </p>
                      <p className="text-2xl font-bold text-yellow-700">
                        {
                          demandes.filter(
                            (d) =>
                              d.statut === "PENDING" ||
                              (d.statut === "En cours" &&
                                !(
                                  d.statut === "Approuvé" ||
                                  d.statut === "CONFIRMED" ||
                                  d.statut === "Refusée"
                                ))
                          ).length
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* Rejetées */}
                <div className="bg-red-50 p-6 rounded-xl border border-red-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm text-red-600 font-medium">
                        Rejetées
                      </p>
                      <p className="text-2xl font-bold text-red-700">
                        {
                          demandes.filter(
                            (d) =>
                              d.statut === "Refusée" ||
                              d.status === "Refusée" ||
                              d.statut === "REJECTED" ||
                              d.status === "REJECTED" ||
                              d.statut === "REFUSED" ||
                              d.status === "REFUSED"
                          ).length
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* Total */}
                <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <span className="text-blue-600 text-lg">📊</span>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm text-blue-600 font-medium">Total</p>
                      <p className="text-2xl font-bold text-blue-700">
                        {demandes.length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <SearchAndFilterBar
                searchTerm={quotationsSearchTerm}
                onSearchChange={setQuotationsSearchTerm}
                filters={quotationsFilterOptions}
                onFilterChange={handleQuotationsFilterChange}
                groupByOptions={quotationsGroupByOptions}
                groupBy={quotationsGroupBy}
                onGroupByChange={setQuotationsGroupBy}
                sortOptions={quotationsSortOptions}
                sortBy={quotationsSortBy}
                onSortChange={setQuotationsSortBy}
                sortDirection={quotationsSortDirection}
                onSortDirectionChange={toggleQuotationsSortDirection}
              />

              {quotationsGroupBy ? (
                renderGroupedData(
                  groupData(getFilteredQuotations(), quotationsGroupBy),
                  (demande) => (
                    <tr key={demande.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        {demande.user?.firstName} {demande.user?.lastName}
                      </td>
                      <td className="px-6 py-4">{demande.serviceType}</td>
                      <td className="px-6 py-4">
                        {demande.departureAddress?.city} →{" "}
                        {demande.arrivalAddress?.city}
                      </td>
                      <td className="px-6 py-4">{demande.serviceDate}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => setDevisModalDemande(demande)}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"
                        >
                          💰 {demande.devis > 0 ? `${demande.devis} $` : "Créer devis"}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold
                  ${
                    demande.status === "APPROVED"
                      ? "bg-green-100 text-green-700"
                      : demande.status === "PENDING"
                      ? "bg-orange-100 text-orange-700"
                      : "bg-red-100 text-red-700"
                  }`}
                          >
                            {demande.status}
                          </span>
                          {demande.devis && demande.devis > 0 && (
                            <span className="text-xs text-blue-600 font-medium">
                              💰 {fmtMontant(demande.devis)} $
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => setSelectedDemande(demande)}
                            className="text-blue-600 hover:text-blue-900 text-sm"
                          >
                            Détails
                          </button>

                          {/* Sélecteur de statut */}
                          <select
                            value={demande.statut || demande.status || ""}
                            onChange={(e) =>
                              handleStatusChangeInTable(
                                demande.id,
                                e.target.value
                              )
                            }
                            className="border rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="En cours">⏳ En cours</option>
                            <option value="Approuvé">✅ Approuvé</option>
                            <option value="Refusée">❌ Refusée</option>
                          </select>
                          {/* Bouton devis / facture */}
                          <button
                            onClick={() => setDevisModalDemande(demande)}
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"
                          >
                            💰 {demande.devis > 0 ? `${demande.devis} $` : "Créer devis"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ),
                  quotationsCurrentPage,
                  setQuotationsCurrentPage
                )
              ) : (
                <>
              <div className="max-w-full overflow-x-auto md:overflow-visible">

  <table className="min-w-full divide-y divide-gray-200 block md:table">
     {/* CORRECTION FINALE */}
    <thead className="sr-only md:not-sr-only md:table-header-group bg-gray-50">
      <tr className="md:table-row">
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase md:table-cell">
          Client
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase md:table-cell">
          Service
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase md:table-cell">
          Trajet
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase md:table-cell">
          Date
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase md:table-cell">
          Devis ($ CAD)
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase md:table-cell">
          Statut
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase md:table-cell">
          Actions
        </th>
      </tr>
    </thead>
    <tbody className="bg-white divide-y divide-gray-200 block md:table-row-group">
  {/* CORRECTION : Parenthèse fermante ajoutée avant .map */}
  {paginateData(getFilteredQuotations(), quotationsCurrentPage).map((demande) => (
    <tr 
      key={demande.id}
      className="hover:bg-gray-50 block md:table-row border-b md:border-0 last:border-b-0 mb-4 md:mb-0"
    >
      {/* Colonne Client */}
      <td 
        data-label="Client" 
        className="px-6 py-4 block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 md:before:hidden"
      >
        {demande.user ? (
          <div>
            <div className="font-medium">
              {demande.user.firstName} {demande.user.lastName}
            </div>
            <div className="text-xs text-green-600 mt-1">✓ Utilisateur connecté</div>
          </div>
        ) : (
          <div>
            <div className="font-medium">
              {demande.clientFirstName} {demande.clientLastName}
            </div>
            <div className="text-xs text-blue-600 mt-1">👤 Utilisateur invité</div>
          </div>
        )}
      </td>

      {/* Colonne Service */}
      <td 
        data-label="Service" 
        className="px-6 py-4 block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 md:before:hidden"
      >
        <span className="inline-flex items-center gap-1">
          {demande.serviceType === "ramassage" && "♻️"}
          {demande.serviceType === "demenagement" && "🚚"}
          {demande.serviceType === "livraison" && "📦"}
          {demande.serviceType}
        </span>
      </td>

      {/* Colonne Trajet */}
      <td 
        data-label="Trajet" 
        className="px-6 py-4 block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 md:before:hidden"
      >
        {demande.departureAddress?.city && demande.arrivalAddress?.city
          ? <div className="flex items-center">
              <span className="text-blue-600">{demande.departureAddress.city}</span>
              <span className="mx-2 text-gray-400">→</span>
              <span className="text-green-600">{demande.arrivalAddress.city}</span>
            </div>
          : demande.pickupAddress?.city
          ? <span className="text-purple-600">{demande.pickupAddress.city}</span>
          : "Ville non renseignée"}
      </td>

      {/* Colonne Date */}
      <td 
        data-label="Date" 
        className="px-6 py-4 block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 md:before:hidden"
      >
        <div className="font-medium">{demande.serviceDate}</div>
       
      </td>

      {/* Colonne Devis */}
      <td 
        data-label="Devis ($ CAD)" 
        className="px-6 py-4 block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 md:before:hidden"
      >
        <button
          onClick={() => setDevisModalDemande(demande)}
          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"
        >
          💰 {demande.devis > 0 ? `${demande.devis} $ CAD` : "Créer devis"}
        </button>
      </td>

      {/* Colonne Statut */}
      <td 
        data-label="Statut" 
        className="px-6 py-4 block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 md:before:hidden"
      >
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold text-center inline-block
            ${
              demande.statut === "APPROVED" ||
              demande.statut === "CONFIRMED" ||
              demande.statut === "Approuvé"
                ? "bg-green-100 text-green-700"
                : demande.statut === "PENDING" || 
                  demande.statut === "En cours" ||
                  demande.statut === "IN_PROGRESS"
                ? "bg-orange-100 text-orange-700"
                : "bg-red-100 text-red-700"
            }`}
        >
          {demande.statut === "APPROVED" || demande.statut === "CONFIRMED" || demande.statut === "Approuvé"
            ? "✅ Confirmé"
            : demande.statut === "PENDING"
            ? "⏳ En attente"
            : demande.statut === "IN_PROGRESS" || demande.statut === "En cours"
            ? "🔄 En cours"
            : demande.statut === "REJECTED" || demande.statut === "Refusé"
            ? "❌ Refusé"
            : demande.statut === "COMPLETED"
            ? "🏁 Terminé"
            : demande.statut || demande.status}
        </span>
      </td>

      {/* Colonne Actions */}
      <td 
        data-label="Actions" 
        className="px-6 py-4 whitespace-nowrap text-sm block md:table-cell before:content-[attr(data-label)] before:block before:font-semibold before:text-gray-700 mb-2 md:before:hidden md:mb-0"
      >
        <div className="flex flex-col gap-2">
          <button
            onClick={() => setSelectedDemande(demande)}
            className="text-blue-600 hover:text-blue-900 text-sm bg-blue-50 px-3 py-1 rounded border border-blue-200 hover:bg-blue-100 transition-colors"
          >
            👁️ Détails
          </button>

          <select
            value={demande.statut || demande.status || ""}
            onChange={(e) =>
              handleStatusChangeInTable(demande.id, e.target.value)
            }
            className="border rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="PENDING">⏳ En attente</option>
            <option value="IN_PROGRESS">🔄 En cours</option>
            <option value="Approuvé">✅ Approuvé</option>
            <option value="Refusée">❌ Refusée</option>
            <option value="COMPLETED">🏁 Terminé</option>
          </select>
        </div>
      </td>
    </tr>
  ))}
</tbody>
  </table>
</div>

                  {getTotalPages(getFilteredQuotations()) > 1 && (
                    <Pagination
                      currentPage={quotationsCurrentPage}
                      totalPages={getTotalPages(getFilteredQuotations())}
                      onPageChange={setQuotationsCurrentPage}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Modal de création d'utilisateur */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl">
            {" "}
            {/* Augmenté à max-w-2xl */}
            <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">
              Créer un nouvel utilisateur
            </h3>
            {/* Grid à 2 colonnes pour réduire la hauteur */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prénom *
                </label>
                <input
                  type="text"
                  value={newUser.firstName}
                  onChange={(e) =>
                    setNewUser((prev) => ({
                      ...prev,
                      firstName: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Prénom"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom *
                </label>
                <input
                  type="text"
                  value={newUser.lastName}
                  onChange={(e) =>
                    setNewUser((prev) => ({
                      ...prev,
                      lastName: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Nom"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) =>
                    setNewUser((prev) => ({ ...prev, email: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="email@exemple.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={newUser.tel}
                  onChange={(e) =>
                    setNewUser((prev) => ({ ...prev, tel: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="+33 1 23 45 67 89"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe *
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) =>
                    setNewUser((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Minimum 6 caractères"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rôle
                </label>
                <select
                  value={newUser.role}
                  onChange={(e) =>
                    setNewUser((prev) => ({ ...prev, role: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="USER">Utilisateur</option>
                  <option value="ADMIN">Administrateur</option>
                </select>
              </div>
            </div>
            {/* Photo de profil en pleine largeur en bas */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Photo de profil (URL)
              </label>
              <input
                type="url"
                value={newUser.profilPicture}
                onChange={(e) =>
                  setNewUser((prev) => ({
                    ...prev,
                    profilPicture: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="https://exemple.com/photo.jpg"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateUser}
                disabled={createLoading}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 font-medium"
              >
                {createLoading ? "Création..." : "Créer l'utilisateur"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal de détails */}
      {showDetailsModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-4 rounded-t-lg flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">
                Détails de la demande
              </h3>
              <button
                onClick={closeDetailsModal}
                className="text-white hover:text-gray-200"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 mb-1">
                    Nom de l'entreprise
                  </h4>
                  <p className="text-lg">{selectedRequest.nom}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-500 mb-1">
                    Téléphone
                  </h4>
                  <p className="text-lg">{selectedRequest.telephone}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-500 mb-1">
                    Email
                  </h4>
                  <p className="text-lg">{selectedRequest.email}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-500 mb-1">
                    Service concerné
                  </h4>

                  <p className="text-lg">{selectedRequest.service}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-500 mb-1">
                    Type de collaboration
                  </h4>
                  <p className="text-lg">{selectedRequest.typeCollaboration}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-500 mb-1">
                    Statut
                  </h4>
                  <span
                    className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                      selectedRequest.statut === "Approuvé"
                        ? "bg-green-100 text-green-800"
                        : selectedRequest.statut === "En cours"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {selectedRequest.statut}
                  </span>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-500 mb-1">
                  Description
                </h4>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-gray-700">
                    {selectedRequest.description ||
                      "Aucune description fournie."}
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={closeDetailsModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {selectedDemande && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[70vh] overflow-y-auto">
            {/* Header de la modale */}
            <div className="bg-blue-600 text-white px-6 py-4 flex justify-between items-center rounded-t-lg sticky top-0 z-10">
              <div>
                <h3 className="text-xl font-bold">
                  Détails demande #{selectedDemande.id.slice(0, 20)}...
                </h3>
                <p className="text-blue-100 text-sm">
                  {selectedDemande.serviceType === "ramassage" &&
                    "♻️ Service de Ramassage"}
                  {selectedDemande.serviceType === "demenagement" &&
                    "🚚 Service de Déménagement"}
                  {selectedDemande.serviceType === "livraison" &&
                    "📦 Service de Livraison"}
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedDemande(null);
                  setZoomedImage(null);
                }}
                className="text-white hover:text-gray-200 text-xl font-bold bg-blue-700 w-8 h-8 rounded-full flex items-center justify-center hover:bg-blue-800 transition-colors"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              {/* Première ligne : Statut + Informations Client + Service */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {/* Statut et Devis */}
                <div className="bg-gray-50 p-4 rounded-lg">
                 <h4 className="font-semibold text-gray-700 text-sm mb-3">
  📊 STATUT
</h4>
<div className="space-y-3">
  <span
    className={`px-3 py-2 rounded-full text-sm font-semibold block text-center ${
      selectedDemande.statut === "APPROVED" ||
      selectedDemande.statut === "CONFIRMED"
        ? "bg-green-100 text-green-800"
        : selectedDemande.statut === "PENDING"
        ? "bg-yellow-100 text-yellow-800"
        : selectedDemande.statut === "IN_PROGRESS"
        ? "bg-blue-100 text-blue-800"
        : selectedDemande.statut === "COMPLETED"
        ? "bg-purple-100 text-purple-800"
        : "bg-red-100 text-red-800"
    }`}
  >
    {selectedDemande.statut === "Approuvé" ||
    selectedDemande.statut === "CONFIRMED"
      ? "✅ Confirmé"
      : selectedDemande.statut === "PENDING"
      ? "⏳ En attente"
      : selectedDemande.statut === "En cours"
      ? "🔄 En cours"
      : selectedDemande.statut === "COMPLETED"
      ? "🏁 Terminé"
      : "❌ Rejeté"}
  </span>

                    {selectedDemande.devis > 0 && (
                      <div className="text-center border-t border-gray-200 pt-3">
                        <p className="text-2xl font-bold text-green-800">
                          {fmtMontant(selectedDemande.devis)} $
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Total du devis</p>
                        {selectedDemande.numeroBonTravail && (
                          <p className="text-xs font-semibold text-gray-600 mt-1">
                            Bon de travail n° {selectedDemande.numeroBonTravail}
                          </p>
                        )}
                        {selectedDemande.devisCreatedAt && (
                          <p className="text-xs text-gray-400">
                            {new Date(
                              selectedDemande.devisCreatedAt
                            ).toLocaleDateString("fr-CA")}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Informations Client */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-700 text-sm mb-3">
                    👤 CLIENT
                  </h4>
                  {selectedDemande.user ? (
                    <div className="text-sm space-y-2">
                      <p>
                        <strong>Nom:</strong> {selectedDemande.user.firstName}{" "}
                        {selectedDemande.user.lastName}
                      </p>
                      <p>
                        <strong>Email:</strong> {selectedDemande.user.email}
                      </p>
                      <p>
                        <strong>Téléphone:</strong>{" "}
                        {selectedDemande.user.tel || "Non renseigné"}
                      </p>
                      <p className="text-green-600 text-xs">
                        ✓ Utilisateur connecté
                      </p>
                    </div>
                  ) : (
                    <div className="text-sm space-y-2">
                      <p>
                        <strong>Nom:</strong> {selectedDemande.clientFirstName}{" "}
                        {selectedDemande.clientLastName}
                      </p>
                      <p>
                        <strong>Email:</strong>{" "}
                        {selectedDemande.clientEmail || "Non renseigné"}
                      </p>
                      <p>
                        <strong>Téléphone:</strong>{" "}
                        {selectedDemande.clientPhone || "Non renseigné"}
                      </p>
                      <p className="text-blue-600 text-xs">
                        👤 Utilisateur invité
                      </p>
                    </div>
                  )}
                </div>

                {/* Informations Service */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-700 text-sm mb-3">
                    📋 SERVICE
                  </h4>
                  <div className="text-sm space-y-2">
                    <p>
                      <strong>Type:</strong>
                      <br />
                      {selectedDemande.serviceType === "ramassage" &&
                        " ♻️ Ramassage"}
                      {selectedDemande.serviceType === "demenagement" &&
                        " 🚚 Déménagement"}
                      {selectedDemande.serviceType === "livraison" &&
                        " 📦 Livraison"}
                    </p>
                    <p>
                      <strong>Date:</strong> {selectedDemande.serviceDate}
                    </p>
                    <p>
                      <strong>Créneau:</strong>{" "}
                      {selectedDemande.timeSlot || "Non spécifié"}
                    </p>
                    {selectedDemande.preferredStartDate && (
                      <p className="text-orange-600 text-xs">
                        <strong>Plage préférée:</strong>
                        <br />
                        {selectedDemande.preferredStartDate.split("T")[0]}
                        {selectedDemande.preferredEndDate &&
                          ` au ${
                            selectedDemande.preferredEndDate.split("T")[0]
                          }`}
                      </p>
                    )}

                    {(selectedDemande.nombreCamions ||
                      selectedDemande.nombreDemenageurs) && (
                      <div className="border-t border-gray-200 pt-2 mt-2 space-y-1">
                        {selectedDemande.nombreCamions && (
                          <p>
                            <strong>🚛 Camions:</strong>{" "}
                            {selectedDemande.nombreCamions}
                          </p>
                        )}
                        {selectedDemande.nombreDemenageurs && (
                          <p>
                            <strong>👷 Déménageurs:</strong>{" "}
                            {selectedDemande.nombreDemenageurs}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Deuxième ligne : Adresse + Articles */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {/* Adresse */}
               {/* Section Adresses - Gère ramassage, déménagement et livraison */}
<div className="space-y-4 mb-6">
  {/* CAS 1: Service de RAMASSAGE (pickupAddress uniquement) */}
  {selectedDemande.serviceType === "ramassage" && selectedDemande.pickupAddress && (
    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
      <h4 className="font-semibold text-purple-700 text-sm mb-3">
        📍 ADRESSE DE RAMASSAGE
      </h4>
      <div className="text-sm space-y-2">
        <p className="font-medium">
          {selectedDemande.pickupAddress.fullAddress}
        </p>
        <p className="text-purple-600">
          {selectedDemande.pickupAddress.city}
          {selectedDemande.pickupAddress.postalCode && 
            `, ${selectedDemande.pickupAddress.postalCode}`}
        </p>
        <div className="grid grid-cols-2 gap-1 text-xs text-gray-600 mt-2">
          <p>
            <strong>Type de logement:</strong>{" "}
            {selectedDemande.pickupAddress.housingType || "Non spécifié"}
          </p>
          <p>
            <strong>Étage:</strong>{" "}
            {selectedDemande.pickupAddress.floor}
          </p>
          <p>
            <strong>Ascenseur:</strong>{" "}
            {selectedDemande.pickupAddress.ascenseur ? "Oui" : "Non"}
          </p>
          <p>
            <strong>Accès difficile:</strong>{" "}
            {selectedDemande.pickupAddress.accesDifficile ? "Oui" : "Non"}
          </p>
        </div>
      </div>
    </div>
  )}

  {/* CAS 2: Service de DÉMÉNAGEMENT (departureAddress + arrivalAddress) */}
  {(selectedDemande.serviceType === "demenagement" || selectedDemande.serviceType === "livraison") && (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Adresse de Départ */}
      {selectedDemande.departureAddress && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-semibold text-blue-700 text-sm mb-3">
            🚚 ADRESSE DE DÉPART
          </h4>
          <div className="text-sm space-y-2">
            <p className="font-medium">
              {selectedDemande.departureAddress.fullAddress}
            </p>
            <p className="text-blue-600">
              {selectedDemande.departureAddress.city}
              {selectedDemande.departureAddress.postalCode && 
                `, ${selectedDemande.departureAddress.postalCode}`}
            </p>
            <div className="grid grid-cols-2 gap-1 text-xs text-gray-600 mt-2">
              <p>
                <strong>Type de logement:</strong>{" "}
                {selectedDemande.departureAddress.housingType || "Non spécifié"}
              </p>
              <p>
                <strong>Étage:</strong>{" "}
                {selectedDemande.departureAddress.floor}
              </p>
              <p>
                <strong>Ascenseur:</strong>{" "}
                {selectedDemande.departureAddress.ascenseur ? "Oui" : "Non"}
              </p>
              <p>
                <strong>Accès difficile:</strong>{" "}
                {selectedDemande.departureAddress.accesDifficile ? "Oui" : "Non"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Adresse d'Arrivée */}
      {selectedDemande.arrivalAddress && (
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h4 className="font-semibold text-green-700 text-sm mb-3">
            🏠 ADRESSE D'ARRIVÉE
          </h4>
          <div className="text-sm space-y-2">
            <p className="font-medium">
              {selectedDemande.arrivalAddress.fullAddress}
            </p>
            <p className="text-green-600">
              {selectedDemande.arrivalAddress.city}
              {selectedDemande.arrivalAddress.postalCode && 
                `, ${selectedDemande.arrivalAddress.postalCode}`}
            </p>
            <div className="grid grid-cols-2 gap-1 text-xs text-gray-600 mt-2">
              <p>
                <strong>Type de logement:</strong>{" "}
                {selectedDemande.arrivalAddress.housingType || "Non spécifié"}
              </p>
              <p>
                <strong>Étage:</strong>{" "}
                {selectedDemande.arrivalAddress.floor}
              </p>
              <p>
                <strong>Ascenseur:</strong>{" "}
                {selectedDemande.arrivalAddress.ascenseur ? "Oui" : "Non"}
              </p>
              <p>
                <strong>Accès difficile:</strong>{" "}
                {selectedDemande.arrivalAddress.accesDifficile ? "Oui" : "Non"}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )}

</div>

              {/* ===== BON DE TRAVAIL / DEVIS ===== */}
              {selectedDemande.devis > 0 ? (
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-amber-800 text-sm">
                      💰 BON DE TRAVAIL
                      {selectedDemande.numeroBonTravail
                        ? ` N° ${selectedDemande.numeroBonTravail}`
                        : ""}
                    </h4>
                    <button
                      onClick={() => setDevisModalDemande(selectedDemande)}
                      className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-semibold"
                    >
                      ✏️ Modifier / Imprimer
                    </button>
                  </div>

                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500 border-b border-amber-200">
                        <th className="text-left font-medium pb-2">Qté</th>
                        <th className="text-left font-medium pb-2">Description</th>
                        <th className="text-right font-medium pb-2">Prix unité</th>
                        <th className="text-right font-medium pb-2">Ligne total</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      <tr className="border-b border-amber-100">
                        <td className="py-2">
                          {selectedDemande.tempsDeplacement ?? "—"}
                        </td>
                        <td>Temps de travail</td>
                        <td className="text-right">
                          {fmtMontant(selectedDemande.prixParHeure)} $
                        </td>
                        <td className="text-right">
                          {fmtMontant(
                            (Number(selectedDemande.prixParHeure) || 0) *
                              (Number(selectedDemande.tempsDeplacement) || 0)
                          )}{" "}
                          $
                        </td>
                      </tr>

                      {selectedDemande.heuresDeplacement > 0 && (
                        <tr className="border-b border-amber-100">
                          <td className="py-2">
                            {selectedDemande.heuresDeplacement}
                          </td>
                          <td>Temps de déplacement</td>
                          <td className="text-right">
                            {fmtMontant(selectedDemande.prixParHeure)} $
                          </td>
                          <td className="text-right">
                            {fmtMontant(
                              (Number(selectedDemande.prixParHeure) || 0) *
                                (Number(selectedDemande.heuresDeplacement) || 0)
                            )}{" "}
                            $
                          </td>
                        </tr>
                      )}

                      {selectedDemande.fraisDeplacement > 0 && (
                        <tr className="border-b border-amber-100">
                          <td className="py-2"></td>
                          <td>Frais de déplacement</td>
                          <td></td>
                          <td className="text-right">
                            {fmtMontant(selectedDemande.fraisDeplacement)} $
                          </td>
                        </tr>
                      )}

                      {selectedDemande.fraisCarburant > 0 && (
                        <tr className="border-b border-amber-100">
                          <td className="py-2"></td>
                          <td>Frais de carburant</td>
                          <td></td>
                          <td className="text-right">
                            {fmtMontant(selectedDemande.fraisCarburant)} $
                          </td>
                        </tr>
                      )}

                      {selectedDemande.sacMatelas > 0 && (
                        <tr className="border-b border-amber-100">
                          <td className="py-2">{selectedDemande.sacMatelas}</td>
                          <td>Sac à matelas</td>
                          <td className="text-right">5,00 $</td>
                          <td className="text-right">
                            {fmtMontant(Number(selectedDemande.sacMatelas) * 5)} $
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <div className="mt-3 ml-auto w-full max-w-xs text-sm">
                    <div className="flex justify-between border-t-2 border-gray-700 py-2 font-bold text-gray-900">
                      <span>TOTAL</span>
                      <span className="text-lg">
                        {fmtMontant(selectedDemande.devis)} $
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-dashed border-gray-300 p-4 rounded-lg mb-6 text-center">
                  <p className="text-sm text-gray-500 mb-2">
                    Aucun devis créé pour cette demande
                  </p>
                  <button
                    onClick={() => setDevisModalDemande(selectedDemande)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"
                  >
                    💰 Créer le bon de travail
                  </button>
                </div>
              )}

                {/* Description manuelle des articles */}
                {selectedDemande.descriptionManuelle && (
                  <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                    <h4 className="font-semibold text-indigo-700 text-sm mb-3">
                      ✍️ DESCRIPTION MANUELLE DU CLIENT
                    </h4>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {selectedDemande.descriptionManuelle}
                    </p>
                  </div>
                )}

                {/* Articles */}
                {selectedDemande.notes && (
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-purple-700 text-sm mb-3">
                      📦 ARTICLES À RAMASSER
                    </h4>
                    <div className="text-sm">
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {selectedDemande.notes}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Troisième ligne : Services + Images */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {/* Services Supplémentaires */}
                {selectedDemande.demandExtraServices &&
                  selectedDemande.demandExtraServices.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-700 text-sm mb-3">
                        🛠️ SERVICES (
                        {selectedDemande.demandExtraServices.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedDemande.demandExtraServices.map(
                          (extraService, index) => {
                            const service = extraService.extraService;
                            return (
                              <div
                                key={extraService.id || index}
                                className="bg-white px-3 py-2 rounded border border-gray-200 text-sm"
                              >
                                <span className="mr-2">
                                  {service?.icon || "⚙️"}
                                </span>
                                {service?.name || "Service"}
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  )}

                {/* Images */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-700 text-sm mb-3">
                    🖼️ IMAGES ({selectedDemande.images?.length || 0})
                  </h4>
                  {selectedDemande.images &&
                  selectedDemande.images.length > 0 ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-3 gap-2">
                        {selectedDemande.images.map((image, index) => {
                          const imageUrl =
                            image.url || image.secure_url || image;
                          return (
                            <div
                              key={image.id || index}
                              className="cursor-pointer transform hover:scale-105 transition-transform"
                              onClick={() =>
                                setZoomedImage({
                                  url: imageUrl,
                                  index: index,
                                  total: selectedDemande.images.length,
                                })
                              }
                            >
                              <img
                                src={imageUrl}
                                alt={`Image ${index + 1}`}
                                className="w-full h-16 object-cover rounded border border-gray-300"
                                onError={(e) => {
                                  e.target.src =
                                    "https://via.placeholder.com/80x60?text=Image";
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500 text-center">
                        👆 Cliquez pour agrandir
                      </p>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center text-sm py-2">
                      Aucune image disponible
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              {/* Actions */}
              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() =>
                    handleStatusChangeInTable(selectedDemande.id, "Approuvé")
                  }
                  disabled={updateLoading}
                  className="px-5 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center gap-2 text-sm font-semibold"
                >
                  {updateLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Mise à jour...
                    </>
                  ) : (
                    "✅ Approuver la demande"
                  )}
                </button>

                <button
                  onClick={() =>
                    handleStatusChangeInTable(selectedDemande.id, "En cours")
                  }
                  disabled={updateLoading}
                  className="px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm font-semibold"
                >
                  {updateLoading ? "..." : "🔄 Marquer en cours"}
                </button>

                <button
                  onClick={() =>
                    handleStatusChangeInTable(selectedDemande.id, "Refusée")
                  }
                  disabled={updateLoading}
                  className="px-5 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 text-sm font-semibold"
                >
                  {updateLoading ? "..." : "❌ Refuser la demande"}
                </button>

                <button
                  onClick={() => setSelectedDemande(null)}
                  className="px-5 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm font-semibold"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Modal d'agrandissement d'images */}
      {zoomedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-[60]">
          <div className="relative max-w-4xl w-full">
            <button
              onClick={() => setZoomedImage(null)}
              className="absolute -top-4 -right-4 text-white text-2xl bg-red-500 w-10 h-10 rounded-full flex items-center justify-center hover:bg-red-600 z-10"
            >
              ×
            </button>

            <img
              src={zoomedImage.url}
              alt="Image agrandie"
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              onError={(e) => {
                e.target.src =
                  "https://via.placeholder.com/800x600?text=Image+Non+Disponible";
              }}
            />

            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-4 py-2 rounded-full">
              {zoomedImage.index + 1} / {zoomedImage.total}
            </div>

            {/* Navigation entre images */}
            {zoomedImage.total > 1 && (
              <>
                {zoomedImage.index > 0 && (
                  <button
                    onClick={() => {
                      const prevIndex = zoomedImage.index - 1;
                      const prevImage = selectedDemande.images[prevIndex];
                      setZoomedImage({
                        url: prevImage.url || prevImage.secure_url || prevImage,
                        index: prevIndex,
                        total: zoomedImage.total,
                      });
                    }}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white text-2xl bg-black bg-opacity-50 w-10 h-10 rounded-full flex items-center justify-center hover:bg-opacity-70"
                  >
                    ‹
                  </button>
                )}

                {zoomedImage.index < zoomedImage.total - 1 && (
                  <button
                    onClick={() => {
                      const nextIndex = zoomedImage.index + 1;
                      const nextImage = selectedDemande.images[nextIndex];
                      setZoomedImage({
                        url: nextImage.url || nextImage.secure_url || nextImage,
                        index: nextIndex,
                        total: zoomedImage.total,
                      });
                    }}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white text-2xl bg-black bg-opacity-50 w-10 h-10 rounded-full flex items-center justify-center hover:bg-opacity-70"
                  >
                    ›
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Devis / Facture */}
      {devisModalDemande && (
        <DevisDetailModal
          demande={devisModalDemande}
          apiBaseUrl={API_BASE_URL}
          onClose={() => setDevisModalDemande(null)}
          onSaved={() => {
            fetchDemandes();
            setDevisModalDemande(null);
          }}
        />
      )}
    </div>
  );
}